import React, { useState, useRef } from 'react';
import { Popover, Tooltip, Typography } from '@material-ui/core';
import { classes } from 'typestyle';
import { ReduxSlice as FilterSlice } from 'src/components/FilterPanel/FilterPanel.container';
import type { ContextFilterConfig } from 'configuration-schemas/confdefn';
import { IconLink } from 'src/components/Headerbar/Links/IconLink';
import styles, { buildFilterIconClass } from 'src/components/Headerbar/Headerbar.styles';

interface FiltersLinkProps {
  filtersEnabled: boolean;
  filterSlice: FilterSlice;
  onFiltersOpen: () => void;
  contextFilterConfig?: ContextFilterConfig;
}

export const FiltersLink = ({ filtersEnabled, filterSlice, onFiltersOpen, contextFilterConfig }: FiltersLinkProps) => {
  const isFiltersEnabled = filterSlice.isFilterSensitive && filtersEnabled;

  const filteredSelections = filterSlice.lastSelections.filter((selection) => {
    if (!contextFilterConfig) return true;

    // Find which group this filter belongs to
    const parentGroup = filterSlice.state.find((group) =>
      group.filters.some((filter) => filter.id === selection.filterDefnId),
    );
    if (!parentGroup) return true;

    const isInBlacklist =
      contextFilterConfig?.groups?.blacklist && contextFilterConfig.groups.blacklist.includes(parentGroup.id);

    return !isInBlacklist;
  });

  const hasFilterSelections = Boolean(
    filterSlice.isFilterSensitive && filteredSelections.length > 0 && isFiltersEnabled,
  );
  const selections = filteredSelections;
  const [isOpen, setPopoverOpen] = useState(false);
  const anchorElement = useRef(null);

  const onLinkClick = () => {
    if (isFiltersEnabled) {
      onFiltersOpen();
    }
    setPopoverOpen(true);
  };

  const handleMouseOver = () => setPopoverOpen(true);
  const handleMouseLeave = () => setPopoverOpen(false);

  return (
    <React.Fragment>
      <Tooltip title={isFiltersEnabled ? '' : 'This tab is not filter sensitive'}>
        <IconLink
          id="FiltersLink"
          iconClass={buildFilterIconClass(hasFilterSelections)}
          linkClass={classes(`${isFiltersEnabled ? '' : 'disabled'}`)}
          text="Filters"
          onClick={onLinkClick}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          ref={anchorElement}
          data-qa="headerbar-filter-icon"
        />
      </Tooltip>
      {isFiltersEnabled && (
        <Popover
          open={isOpen}
          anchorEl={anchorElement.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          classes={{ root: styles.filterPopover }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          disableRestoreFocus={true}
        >
          <Typography>Selected Filters ({selections.length}):</Typography>
          <ul>
            {selections.map((filterSelection) => {
              return <li key={filterSelection.id}>{filterSelection.id}</li>;
            })}
          </ul>
        </Popover>
      )}
    </React.Fragment>
  );
};
